import React, { useState } from 'react';
import { v4 as uuid } from 'uuid';
import 'twin.macro';
import { Eleve } from 'types/eleves';
import Button from 'components/Buttons/Button';
import { PlusIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import { GroupTable } from './GroupTable';
import { GroupCreator } from './GroupCreator';
import { useTranslation } from 'react-i18next';
import { EleveGroup } from 'types/eleveGroups';
import { EditingGroup } from './types';
import { IndividualElevesPanel } from '../IndividualElevesPanel';
import { handleEphemeralGroupSubmit } from '../groupHandlers';

// Editor mode type for clearer state management
type EditorMode =
  | { type: 'none' }
  | { type: 'creating-regular'; group: EditingGroup }
  | { type: 'editing-regular'; group: EditingGroup }
  | { type: 'creating-ephemeral' }
  | { type: 'editing-ephemeral'; groupId: string };

interface Props {
  eleves: Eleve[];
  groupIds: string[];
  onChange: (groupIds: string[]) => void;
  groups: EleveGroup[];
  onCreateGroup?: (g: EleveGroup) => void;
  onUpdateGroup?: (g: EleveGroup) => void;
  onDeleteGroup?: (id: string) => void;
  pendingEphemeralGroups?: Array<{ id: string; elevesIds: string[] }>;
  onPendingEphemeralGroupsChange?: (
    groups: Array<{ id: string; elevesIds: string[] }>,
  ) => void;
}

export function GroupEditor(props: Props) {
  const {
    onChange,
    groups,
    onCreateGroup,
    onUpdateGroup,
    onDeleteGroup,
    eleves,
    groupIds,
    pendingEphemeralGroups = [],
    onPendingEphemeralGroupsChange,
  } = props;
  const { t } = useTranslation(['cahierJournal']);

  const [editorMode, setEditorMode] = useState<EditorMode>({ type: 'none' });

  function getEphemeralGroupEleveIds(groupId: string): string[] {
    const pendingGroup = pendingEphemeralGroups.find(g => g.id === groupId);
    if (pendingGroup) {
      return pendingGroup.elevesIds;
    }

    const savedGroup = groups.find(g => g.id === groupId && g.isEphemeral);
    if (savedGroup) {
      return savedGroup.eleves.map(e => e.id);
    }

    return [];
  }

  function handleRegularGroupSubmit(group: EditingGroup) {
    if (!group.id) {
      onCreateGroup?.({
        ...group,
        id: uuid(),
      });
    } else {
      onUpdateGroup?.({
        ...group,
        id: group.id,
      });
    }
    setEditorMode({ type: 'none' });
  }

  function handleRegularGroupDelete(groupId?: string) {
    if (groupId) {
      onDeleteGroup?.(groupId);
    }
    setEditorMode({ type: 'none' });
  }

  function handleEphemeralGroupSubmitLocal(
    selectedIds: string[],
    editingGroupId?: string,
  ) {
    handleEphemeralGroupSubmit(
      selectedIds,
      editingGroupId,
      groups,
      pendingEphemeralGroups,
      eleves,
      onUpdateGroup,
      onPendingEphemeralGroupsChange,
    );
    setEditorMode({ type: 'none' });
  }

  function handleEphemeralGroupDelete(groupId: string) {
    const updatedPendingGroups = pendingEphemeralGroups.filter(
      g => g.id !== groupId,
    );
    onPendingEphemeralGroupsChange?.(updatedPendingGroups);

    onChange(groupIds.filter(id => id !== groupId));

    onDeleteGroup?.(groupId);

    setEditorMode({ type: 'none' });
  }

  function handleEditGroup(groupId: string) {
    const matchingGroup = groups.find(g => g.id === groupId);
    if (!matchingGroup) {
      return;
    }

    if (matchingGroup.isEphemeral) {
      setEditorMode({ type: 'editing-ephemeral', groupId });
    } else {
      setEditorMode({ type: 'editing-regular', group: matchingGroup });
    }
  }

  switch (editorMode.type) {
    case 'creating-regular':
      return (
        <GroupCreator
          eleves={eleves}
          onDelete={() => handleRegularGroupDelete()}
          onChange={group => {
            setEditorMode({ type: 'creating-regular', group });
          }}
          onSubmit={() => handleRegularGroupSubmit(editorMode.group)}
          onCancel={() => setEditorMode({ type: 'none' })}
          value={editorMode.group}
        />
      );

    case 'editing-regular':
      return (
        <GroupCreator
          eleves={eleves}
          onDelete={() => handleRegularGroupDelete(editorMode.group.id)}
          onChange={group => setEditorMode({ type: 'editing-regular', group })}
          onSubmit={() => handleRegularGroupSubmit(editorMode.group)}
          onCancel={() => setEditorMode({ type: 'none' })}
          value={editorMode.group}
        />
      );

    case 'creating-ephemeral':
      return (
        <IndividualElevesPanel
          eleves={eleves}
          selectedEleveIds={[]}
          isEditing={false}
          onSubmit={selectedIds => handleEphemeralGroupSubmitLocal(selectedIds)}
          onClose={() => setEditorMode({ type: 'none' })}
        />
      );

    case 'editing-ephemeral': {
      const initialSelectedIds = getEphemeralGroupEleveIds(editorMode.groupId);
      return (
        <IndividualElevesPanel
          eleves={eleves}
          selectedEleveIds={initialSelectedIds}
          isEditing={true}
          onDelete={() => handleEphemeralGroupDelete(editorMode.groupId)}
          onSubmit={selectedIds =>
            handleEphemeralGroupSubmitLocal(selectedIds, editorMode.groupId)
          }
          onClose={() => setEditorMode({ type: 'none' })}
        />
      );
    }

    case 'none':
    default:
      return (
        <div tw="space-y-4 py-4">
          {groups.length > 0 && (
            <GroupTable
              groupIds={groupIds}
              onChange={onChange}
              groups={groups}
              eleves={eleves}
              onEditGroup={handleEditGroup}
            />
          )}
          <div tw="flex items-center space-x-2">
            <Button
              small
              minimal
              onClick={() =>
                setEditorMode({
                  type: 'creating-regular',
                  group: { name: '', eleves: [], color: 'gray-200' },
                })
              }
              icon={PlusIcon}
            >
              {t('ateliers.elevesGroup.buttons.addGroup')}
            </Button>
            <Button
              small
              minimal
              onClick={() => setEditorMode({ type: 'creating-ephemeral' })}
              icon={UserPlusIcon}
            >
              {t('ateliers.addIndividualStudents')}
            </Button>
          </div>
        </div>
      );
  }
}
